import { createSortableTextHeader } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnHeader/ColumnHeader';
import { CapTableSelect } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineCapSelect/CapTableSelect';
import { TableFilterCheckboxSelect } from '@dealstack/frontend/shared/components/Table/MantineTable/TableFilterCheckboxSelect';
import { TextInputFilter } from '@dealstack/frontend/shared/components/Table/MantineTable/TextInputFilter/TextInputFilter';
import {
	ExtendedColumn,
	ExtendedColumnDef,
	MantineFilterMultiSelectProps,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { RecordAny } from '@dealstack/shared/lib/common/common';
import { Flex, Text } from '@mantine/core';
import styled from 'styled-components';
import { ITextProps } from '@dealstack/frontend/shared/components/fundament/Text/Text.types';

type Props<T extends RecordAny> = ExtendedColumnDef<T> & {
	accessorKey?: keyof T;
	showFooter?: boolean;
	showFilter?: boolean;
	footerValue?: string | number;
	text?: string;
	filterText?: string;
	headerText?: string;
	cellTextProps?: ITextProps;
	enableSorting?: boolean;
	mantineFilterMultiSelectProps?: MantineFilterMultiSelectProps;
	mantineFilterSelectProps?: {
		data: { label: string; value: string }[];
	};
};
export const createTextColumn = <T extends RecordAny>({
	showFooter = false,
	cellTextProps = {},
	footerValue,
	text,
	filterText,
	headerText,
	enableSorting = true,
	mantineFilterMultiSelectProps,
	mantineFilterSelectProps,
	...columnDef
}: Props<T>): ExtendedColumnDef<T> => {
	const result = {
		...columnDef,
		header: headerText ? createSortableTextHeader(headerText, enableSorting) : columnDef.header,
		columnLabel: columnDef.columnLabel ?? headerText ?? '',
		enableSorting,
		accessorKey: columnDef.accessorKey ?? columnDef.id ?? 'text',
		cell:
			columnDef.cell ??
			(({ cell }) => (
				<Flex align="center" justify="start" w="100%" miw="0">
					<Text
						size="B6Medium"
						style={{
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							display: 'block',
						}}
						{...cellTextProps}
					>
						{cell.getValue<string>()}
					</Text>
				</Flex>
			)),
		footer: showFooter
			? columnDef.footer ??
				(() => (
					<DetailsDiv>
						<UniqueDiv>{text}</UniqueDiv>
						<InfoDiv>{footerValue}</InfoDiv>
					</DetailsDiv>
				))
			: undefined,
		filter: columnDef.showFilter
			? ({ column }) => {
					if (columnDef.filterVariant === 'multi-select') {
						return (
							<TableFilterCheckboxSelect
								column={column as ExtendedColumn<unknown>}
								filterText={filterText}
								multiple
							/>
						);
					}

					if (columnDef.filterVariant === 'select') {
						return (
							<CapTableSelect
								column={column as ExtendedColumn<unknown>}
								filterText={filterText}
							/>
						);
					}

					return <TextInputFilter column={column} filterText={filterText} />;
				}
			: undefined,
		mantineFilterMultiSelectProps,
		mantineFilterSelectProps,
	} as ExtendedColumnDef<T>;

	return result;
};

export const DetailsDiv = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	font-size: 14px;
	line-height: 20px;
`;

export const TotalDetailsDiv = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-end;
	font-size: 14px;
	line-height: 20px;
	width: 100%;
`;

export const UniqueDiv = styled.div`
	font-weight: 400;
`;

export const InfoDiv = styled.div`
	font-weight: 600;
`;
